<!-- トークメッセージコンポーネント -->
<script setup lang="ts">
  import _ from "lodash";
  import { storeToRefs } from "pinia";
  import { useLoginStore } from "~/stores/LoginStore";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useTalkRoomTmpViewStore } from "~/stores/TalkRoomTmpViewStore";
  import { useTalkUserDataStore } from "~/stores/TalkUserDataStore";
  import { Talk } from "~/entities/Talk";
  import { TalkRoomUser } from "~/entities/TalkRoomUser";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { useUserStore } from "~/stores/UserStore";
  import { TALK_BOOKMARK_PER_ROOM_MAX } from "~/consts/TalkConfig";

  // ogp説明最大文字数
  const ogpDescriptionMaxLength = 50;

  const userStore = useUserStore();

  // スナックバーstore
  const snackBarStore = useSnackBarStore();

  // レスポンシブ対応
  const isTabletAndSp = isTabletAndSpDisplayRef();

  interface Props {
    canAction: boolean;
    planId: string;
    talkRoomId: string;
    talk: Talk;
    canLike: boolean;
    isOwner: boolean; // 自分が講師かどうか
    isOwnerOrMember: boolean; // 自分が講師・入会者かどうか
    imageTalks?: Talk[];
    ownerIds: string[];
    userGoodList: string[];
    scroll: (targetTalkId: string) => void;
    searchWord: string | null; // 背景色を塗るため
    isThreadView?: boolean; // スレッド表示中かどうか
    isTest?: boolean; // テスト時のみ true にする
  }
  interface Emits {
    (event: "onLikeClick", talk: Talk): void;
    (event: "onUserClick", talk: TalkRoomUser): void;
  }
  const props = withDefaults(defineProps<Props>(), {
    canLike: false,
    imageTalks: () => [],
    isThreadView: false,
  });
  defineEmits<Emits>();

  // TODO date の場合は、AtomsTalkDate を使うこと
  if (props.talk.messageType === "date") {
    consoleError("TalkMessage.vue: date type support is deprecated");
  }

  const loginStore = useLoginStore();
  const talkRoomStore = useTalkRoomStore();
  const talkRoomTmpViewStore = useTalkRoomTmpViewStore();
  const talkUserDataStore = useTalkUserDataStore();

  const { talkRoomUsers } = storeToRefs(talkRoomStore);

  // ログインユーザーID
  const loginUserId = computed<string>(() => loginStore.loginInfo.userId);

  // 自分のトークかどうか
  const isMe = computed<boolean>(() => props.talk.userId === loginStore.loginInfo?.userId);
  // オーナーのトークかどうか
  const isOwnersTalk = computed<boolean>(() => !!props.talk.userId && props.ownerIds.includes(props.talk.userId));

  // 投稿者情報
  const postUser = computed<TalkRoomUser>(() => talkRoomUsers.value[props.talk.userId]);
  const postTime = computed<string>(() => FormatDateString(props.talk.postDateTime, "HH:mm:ss"));

  // 返信元ユーザー情報
  const replyUser = computed<TalkRoomUser | undefined>(() => {
    return props.talk.replyTalk ? talkRoomUsers.value[props.talk.replyTalk.userId] : undefined;
  });

  // https://fincs.backlog.com/view/FINCS-2653 デバッグ用
  if (props.talk.messageType !== "date" && !postUser.value) {
    // production はログが多すぎるので除外
    if (useRuntimeConfig().public.appEnv !== "production") {
      sentryErrorLog(`[FINCS-2653] postUser is null: ${props.talk.userId}`, props.talk, talkRoomUsers.value);
    }
  }
  if (props.talk.replyTalk?.userId && !replyUser.value) {
    sentryErrorLog(`[FINCS-2653] replyUser is null: ${props.talk.replyTalk.userId}`, props.talk, talkRoomUsers.value);
  }

  // メッセージスレッド関連
  const useThread = featureTalkThread();
  const { showThread, threadTalk, threadTargetTalkId } = storeToRefs(talkRoomTmpViewStore);
  const onClickThread = (talk: Talk) => {
    if (talk.isThreadParent()) {
      // スレッド親の場合、自分をスレッドのベースに設定
      threadTalk.value = talk;
      threadTargetTalkId.value = undefined; // 末尾へスクロールして表示
    } else {
      // スレッド子の場合、親をスレッドのベースに設定
      threadTalk.value = talk.threadTalk!;
      threadTargetTalkId.value = talk.talkId; // 自分へスクロールして表示
    }
    showThread.value = true;
  };

  // メッセージ編集関連
  const showEditConfirm = ref(false);

  // メッセージ削除関連
  const showDeleteConfirm = ref(false);
  const onClickDelete = (talk: Talk) => {
    if (talk.isDeleted()) {
      snackBarStore.setError("すでに削除済みです");
      return;
    }
    showDeleteConfirm.value = true;
  };
  const deleteTalk = async (): Promise<void> => {
    showDeleteConfirm.value = false;
    // 投稿禁止チェック
    if (userStore.isPostProhibited()) {
      return;
    }
    try {
      await talkRoomStore.deleteTalk(props.talk.talkId);
      snackBarStore.set("メッセージを削除しました");
    } catch (error: any) {
      snackBarStore.setError("メッセージの削除に失敗しました");
      sentryErrorLog(`メッセージ削除エラー: ${error.message}`);
    }
  };

  const isLiked = computed<boolean>(() => props.userGoodList.includes(props.talk.talkId));

  // 返信アイコン表示
  const showReplyIcon = ref(false);

  const showImageModal = ref(false);
  const showPdfModal = ref(false);
  const showPdfSrc = ref("");

  const decorateLinks = (src: string) => {
    // URLをリンク化
    return src.replace(/(https?:\/\/[^\s]*)/g, url => {
      // 同サイト内であれば同タブ、別サイトであれば新規タブで開く
      return url.startsWith(window.location.origin)
        ? `<a href='${url}' class='message_link'>${url}</a>`
        : `<a href='${url}' class='message_link' target='_blank'>${url}</a>`;
    });
  };
  const decorateBySearchWord = (src: string) => {
    // 検索ワードをハイライト
    return props.searchWord
      ? src.replace(new RegExp(props.searchWord, "g"), `<span class="search_word">${props.searchWord}</span>`)
      : src;
  };
  const decoratedPdfFileName = computed<string>(() => {
    // ファイル名内の検索ワードをハイライト
    return decorateBySearchWord(props.talk.fileName);
  });
  const decorateText = (src: string, mentionUserIds: string[] | undefined, isUpdated: boolean) => {
    if (!_.isString(src)) {
      return "";
    }
    return (
      decorateByMention(
        decorateBySearchWord(decorateLinks(src)),
        mentionUserIds,
        talkRoomUsers.value,
        isMe.value ? "#e6f411" : undefined,
      ) + (isUpdated ? "<span class='update_mark'>（編集済み）</span>" : "")
    );
  };
  const decoratedMessage = computed<string>(() => {
    return decorateText(props.talk.contents, props.talk.mentionUserIds, !!props.talk.updateDateTime);
  });
  const decoratedNote = computed<string>(() => {
    return props.talk.note ? decorateText(props.talk.note, props.talk.mentionUserIds, !!props.talk.updateDateTime) : "";
  });
  // いいね処理中かどうか
  const isGooding = ref<boolean>(false);
  const isLikeActioned = ref(false);
  const likeAction = async () => {
    if (!props.canLike) return;
    // 投稿禁止チェック
    if (userStore.isPostProhibited()) {
      return;
    }
    // 連打防止
    if (isGooding.value) {
      return;
    }
    isGooding.value = true;

    try {
      if (isLiked.value) {
        await talkRoomStore.deleteGood(props.planId, props.talkRoomId, props.talk.talkId, loginStore.loginInfo?.userId);
        isLikeActioned.value = false;
      } else {
        await talkRoomStore.sendGood(props.planId, props.talkRoomId, props.talk.talkId, loginStore.loginInfo?.userId);
        isLikeActioned.value = true;
      }
    } catch (error: any) {
      sentryErrorLog(`いいねエラー: ${error}`);
    } finally {
      isGooding.value = false;
    }
  };

  const clickContentsUrl = async () => {
    if (props.talk.messageType === "pdf") {
      // 先にモーダルは表示しておく
      showPdfSrc.value = "";
      showPdfModal.value = true;

      // 講師・会員の場合は署名付き cookie で CFn アクセス
      // 非会員の場合は署名付き URL で S3 アクセス
      const url = props.isOwnerOrMember
        ? ConvertS3SignedUrlToCFnUrl(props.talk.contentsUrl)
        : await talkRoomStore.checkContentsUrl(props.talk);
      if (url) {
        showPdfSrc.value = url;
      } else {
        showPdfModal.value = false;
        snackBarStore.set("ファイルの取得に失敗しました", "error");
      }
    } else {
      // モーダル表示。あとはモーダル内で処理させる
      showImageModal.value = true;
    }
  };

  // メッセージエリア全体のクリックイベント
  const onClick = () => {
    // タブレット以下の場合は返信アイコンの表示を切り替える
    if (isTabletAndSp) {
      showReplyIcon.value = !showReplyIcon.value;
    }
  };

  // 署名付きURL期限チェック＆更新はトークリスト取得時に対応済み
  const thumbnailImageSrc = computed(() => {
    return props.talk.thumbnailImageUrl;
  });

  const canBookmark = computed<boolean>(() => {
    return !!loginUserId.value;
  });

  const onClickBookmark = async (talk: Talk) => {
    if (!loginUserId.value) {
      return;
    }
    try {
      if (talk.bookmarked) {
        await talkUserDataStore.setBookmark(loginUserId.value, props.planId, props.talkRoomId, talk.talkId, false);
        snackBarStore.set("ブックマークを削除しました");
      } else {
        const result = await talkUserDataStore.setBookmark(
          loginUserId.value,
          props.planId,
          props.talkRoomId,
          talk.talkId,
          true,
        );
        if (result) {
          snackBarStore.set("ブックマークに追加しました");
        } else {
          snackBarStore.setError(
            `ブックマークはチャンネル/DMごとに${TALK_BOOKMARK_PER_ROOM_MAX}個までしか設定できません`,
          );
        }
      }
    } catch (e: any) {
      snackBarStore.setError("ブックマークの設定に失敗しました");
    }
  };

  const onClickCopyLink = (talk: Talk) => {
    const url = `${location.origin}${talk.getPath(props.planId)}`;
    navigator.clipboard.writeText(url).then(() => {
      snackBarStore.set("リンクをコピーしました");
    });
  };

  const talkMessageRef = ref<HTMLElement | null>(null);
  const animateFound = () => {
    setTimeout(() => {
      talkMessageRef.value!.classList.add("talk-found");
      setTimeout(() => {
        talkMessageRef.value!.classList.remove("talk-found");
      }, 500);
    }, 200);
  };

  defineExpose({
    talkId: props.talk.talkId,
    animateFound,
  });
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    ref="talkMessageRef"
    class="wrapper"
    :class="{
      isDate: talk.messageType === 'date',
      isMessage: talk.messageType !== 'date',
      isMe: talk.messageType !== 'date' && isMe,
      isOwnersTalk: talk.messageType !== 'date' && !isMe && isOwnersTalk,
    }"
    @click="onClick"
  >
    <template v-if="talk.messageType === 'date'">
      <div class="datepart">{{ talk.date }}</div>
    </template>
    <template v-else>
      <div class="header">
        <div>
          <div class="content_wrapper d-flex">
            <div v-if="!isMe" class="avator">
              <AtomsUserIcon
                :src="postUser?.userImage"
                :is-owner="isOwnersTalk"
                :width="40"
                :height="40"
                data-testid="talk-message-avator"
                @click="$emit('onUserClick', postUser)"
              ></AtomsUserIcon>
            </div>
            <div style="width: 100%">
              <div class="d-flex mb-1">
                <div v-if="!isMe" class="action_by mr-4">
                  {{ postUser?.userName }}
                </div>
                <div class="action_at">
                  {{ postTime }}
                </div>
              </div>

              <!-- スレッド内メッセージ表示中、それがチャンネルに表示されているか -->
              <div v-if="useThread && isThreadView && talk.isThreadChild() && talk.showInRoom" class="show-in-room">
                チャンネルにも表示中
              </div>

              <div class="content_balloon_wrapper">
                <!-- 返信メッセージ -->
                <MoleculesTalkMessageRefSrc
                  v-if="talk.replyTalk"
                  class="ref_src_reply"
                  type="reply"
                  :ref-src-talk="talk.replyTalk"
                  :is-me="isMe"
                  :talk-room-users="talkRoomUsers"
                  :owner-ids="props.ownerIds"
                  :scroll="props.scroll"
                />

                <!-- スレッドメッセージ -->
                <template v-if="useThread && talk.isThreadChild()">
                  <MoleculesTalkMessageRefSrc
                    v-if="!isThreadView && talk.threadTalk"
                    type="thread"
                    :ref-src-talk="talk.threadTalk"
                    :is-me="isMe"
                    :talk-room-users="talkRoomUsers"
                    :owner-ids="props.ownerIds"
                    :scroll="props.scroll"
                  />
                </template>

                <!-- 削除メッセージ (スレッド表示時に親トークが削除済みの場合) -->
                <div
                  v-if="talk.isDeleted() && talk.isThreadParent()"
                  class="content isText"
                  style="background-color: rgb(var(--v-theme-monochrome-03))"
                >
                  メッセージが削除されました
                </div>

                <!-- テキストメッセージ -->
                <div v-else-if="talk.messageType === 'text'" class="content isText">
                  <div v-html="sanitizeDom(decoratedMessage)"></div>
                  <a
                    v-for="(ogp, ogpIndex) in talk.ogpList"
                    :key="ogpIndex"
                    :href="ogp.url"
                    target="_blank"
                    class="ogp"
                  >
                    <div class="ogp_info">
                      <div class="ogp_title">{{ ogp.title }}</div>
                      <!-- 調整中につき、ひとまず非表示に -->
                      <div v-if="false" class="ogp_description">
                        {{
                          ogp.description?.length > ogpDescriptionMaxLength
                            ? ogp.description.substring(0, ogpDescriptionMaxLength) + "..."
                            : ogp.description
                        }}
                      </div>
                    </div>
                    <div v-if="ogp.image" class="ogp_img_wrapper">
                      <v-img :src="ogp.image" class="ogp_img" alt="ogp" />
                    </div>
                  </a>
                </div>

                <!-- 画像メッセージ -->
                <div v-else-if="talk.messageType === 'image'" class="content isText">
                  <div style="cursor: pointer" @click.stop="clickContentsUrl">
                    <v-img :src="thumbnailImageSrc" class="content isImage" height="320" alt="thumbnail" />
                  </div>
                  <div v-if="talk.note" class="mt-2" v-html="sanitizeDom(decoratedNote)"></div>
                </div>

                <!-- PDFメッセージ -->
                <div v-else-if="talk.messageType === 'pdf'" class="content isText">
                  <AtomsPdfFileLabel
                    :file-name="decoratedPdfFileName!"
                    :has-preview-button="true"
                    @click="clickContentsUrl"
                  />
                  <div v-if="talk.note" class="mt-2" v-html="sanitizeDom(decoratedNote)"></div>
                </div>
              </div>

              <div class="actions d-flex" :class="isMe ? 'justify-end' : 'justify-start'">
                <!-- いいね -->
                <div
                  class="action_like mr-2 my-1"
                  :class="{ isLiked: isLiked, hasLiked: talk.goodCount > 0 }"
                  data-testid="like-talk-message"
                >
                  <div class="action_like_icon" :class="{ likeAnime: isLikeActioned }" @click.stop="likeAction">
                    <AtomsSvgIcon
                      :img-path="`assets/images/icon/talk-like${isLiked ? '-active' : ''}.svg`"
                      :width="18"
                      :height="18"
                    ></AtomsSvgIcon>
                    <span id="chakra1" :class="{ likeAnime: isLikeActioned }"></span>
                    <span id="chakra2" :class="{ likeAnime: isLikeActioned }"></span>
                    <span id="chakra3" :class="{ likeAnime: isLikeActioned }"></span>
                    <span id="chakra4" :class="{ likeAnime: isLikeActioned }"></span>
                    <span id="chakra5" :class="{ likeAnime: isLikeActioned }"></span>
                    <span id="chakra6" :class="{ likeAnime: isLikeActioned }"></span>
                    <span id="chakra7" :class="{ likeAnime: isLikeActioned }"></span>
                    <span id="chakra8" :class="{ likeAnime: isLikeActioned }"></span>
                    <span id="chakra9" :class="{ likeAnime: isLikeActioned }"></span>
                    <span id="chakra10" :class="{ likeAnime: isLikeActioned }"></span>
                  </div>
                  <div v-if="talk.goodCount" class="action_like_divider" data-testid="like-talk-message-divider"></div>
                  <div
                    v-if="talk.goodCount"
                    class="action_like_size"
                    data-testid="like-talk-message-count"
                    @click.stop="$emit('onLikeClick', talk)"
                  >
                    {{ talk.goodCount }}
                  </div>
                </div>
                <!-- 返信 -->
                <!-- TODO スレッド表示時要対応 -->
                <div v-if="canAction && !isThreadView" class="action_reply">
                  <AtomsSvgIcon
                    class="reply_icon"
                    img-path="assets/images/icon/talk-reply.svg"
                    :width="18"
                    :height="18"
                    data-testid="reply-talk-message"
                    @click="talkRoomStore.replyTalkToEdit = talk"
                  ></AtomsSvgIcon>
                </div>
                <!-- スレッド -->
                <div v-if="useThread && !isThreadView && canAction" class="action_edit">
                  <v-icon
                    class="action_icon"
                    icon="$mdiMessageText"
                    :size="20"
                    data-testid="thread-talk-message"
                    @click.stop="onClickThread(talk)"
                  />
                </div>
                <!-- ブックマーク -->
                <div v-if="canBookmark" class="action_edit">
                  <v-icon
                    class="action_icon"
                    icon="$mdiBookmark"
                    :color="talk.bookmarked ? `rgb(17, 156, 246)` : ``"
                    :size="20"
                    data-testid="bookmark-talk-message"
                    @click.stop="onClickBookmark(talk)"
                  />
                </div>
                <!-- 編集 -->
                <div v-if="canAction && isMe" class="action_edit">
                  <v-icon
                    class="action_icon"
                    icon="$mdiPencil"
                    :size="20"
                    data-testid="edit-talk-message"
                    @click.stop="showEditConfirm = true"
                  />
                </div>
                <!-- 削除 -->
                <div v-if="canAction && (isMe || isOwner)" class="action_delete">
                  <AtomsSvgIcon
                    class="delete_icon"
                    img-path="assets/images/icon/talk-delete.svg"
                    :width="18"
                    :height="18"
                    data-testid="delete-talk-message"
                    @click.stop="onClickDelete(talk)"
                  ></AtomsSvgIcon>
                </div>
                <!-- リンクコピー -->
                <div class="action_edit">
                  <v-icon
                    class="action_icon"
                    icon="$mdiLinkVariant"
                    :size="20"
                    data-testid="copy-link-talk-message"
                    @click.stop="onClickCopyLink(talk)"
                  />
                </div>
              </div>

              <!-- スレッド情報（コメント数など） -->
              <MoleculesTalkMessageThreadInfo
                v-if="useThread && !isThreadView && talk.threadComments >= 1"
                :talk="talk"
                @click="onClickThread(talk)"
              ></MoleculesTalkMessageThreadInfo>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- トーク編集用ダイアログ -->
    <OrganismsTalkEditDialog
      v-if="showEditConfirm"
      v-model:open="showEditConfirm"
      :talk="talk"
      :is-test="isTest"
    ></OrganismsTalkEditDialog>

    <!-- トーク削除用ダイアログ -->
    <MoleculesYesNoDialog
      v-model:is-open="showDeleteConfirm"
      message="メッセージを削除しますか？"
      @click-yes="deleteTalk"
      @click-no="showDeleteConfirm = false"
    />

    <AtomsTalkImageModal
      v-if="talk.messageType === 'image' && showImageModal"
      v-model:isOpen="showImageModal"
      :talk-index="imageTalks.indexOf(talk)"
      :image-talks="imageTalks"
    />
    <AtomsTalkPdfModal
      v-if="talk.messageType === 'pdf' && showPdfSrc && showPdfModal"
      v-model:isOpen="showPdfModal"
      :url="showPdfSrc"
      :is-signed-cookie="true"
    />
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .wrapper {
    padding-top: 10px;
    &.isDate {
      .datepart {
        width: 67px;
        border-radius: 20px;
        background-color: #333333;
        font-size: 11px;
        text-align: center;
        padding: 3px 0 5px;
        margin: 0 auto;
      }
    }
    &.isMessage {
      .header {
        display: flex;
        align-items: flex-start;
        margin-bottom: 3px;
        .avator {
          padding: 6px 10px 0 0;
          cursor: pointer;
        }
        .content_wrapper {
          position: relative;
          min-width: 240px;
          .action_by {
            font-size: 13px;
            font-weight: 700;
            line-height: 22px;
            text-align: left;
          }
          .action_at {
            font-size: 11px;
            font-weight: 400;
            line-height: 22px;
            text-align: left;
          }
        }
        .content_balloon_wrapper {
          border-radius: 0px 10px 10px 10px;
          overflow: hidden;
        }
        .content {
          background-color: #161616;
          max-width: 600px;
          &.isText {
            font-size: 14px;
            padding: 10px 15px;
            word-break: break-all;
            white-space: break-spaces;
            color: white;
            > a,
            > div > a {
              color: #119cf6;
            }
            .ogp {
              display: table;
              margin-top: 10px;
              text-decoration: none;
              .ogp_info {
                display: table-cell;
                vertical-align: middle;
                border-left: 4px solid #666666;
                padding: 0 10px;
                .ogp_title {
                  font-size: 12px;
                  font-weight: 400;
                  line-height: 1.8em;
                  letter-spacing: 0.06em;
                  text-align: left;
                  color: white;
                  margin-top: -4px;
                }
                .ogp_description {
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 1.8em;
                  letter-spacing: 0.06em;
                  text-align: left;
                  color: #aaaaaa;
                }
              }
              .ogp_img_wrapper {
                display: table-cell;
                height: 40px;
                .ogp_img {
                  width: 80px;
                  height: 40px;
                  object-fit: contain;
                }
              }
            }
          }
          &.isImage {
            background: transparent !important;
            max-height: 300px;
            max-width: calc(100vw - 150px);
          }
        }
        .actions {
          .action_delete,
          .action_edit,
          .action_reply,
          .action_like {
            background: rgba(22, 22, 22, 1);
            height: 32px;
            border-radius: 18px;
            display: flex;
            &.isLiked {
              color: #0e9cf6;
            }
            &.hasLiked,
            &.isLiked {
              padding: 6px 2px 6px 6px;
              align-items: center;
              .action_like_icon {
                padding: 0 2px 0 4px;
                cursor: pointer;
              }
            }
            .action_icon {
              opacity: 0.5;
            }
            .action_like_icon {
              margin: 0 5px 0 0;
              cursor: pointer;
            }
            .action_like_size {
              padding-right: 10px;
              padding-left: 8px;
              font-size: 14px;
              cursor: pointer;
            }
            .action_like_divider {
              width: 1px;
              height: 16px;
              margin-top: 2px;
              background-color: #666666;
            }
          }
          .action_like {
            padding: 6px 2px 0 6px;
          }
          .action_reply {
            margin-top: 4px;
            padding: 8px 6px;
            cursor: pointer;
          }
          .action_edit {
            display: block;
            margin: 4px 0 4px 8px;
            padding: 0 8px;
            cursor: pointer;
          }
          .action_delete {
            display: block;
            margin: 4px 0 4px 8px;
            padding: 6px 8px;
            cursor: pointer;
          }
        }
      }
      &.isMe {
        .header {
          flex-direction: row-reverse;
          .avator {
            padding: 0 0 0 5px;
          }
          .content_wrapper {
            .action_at {
              margin-left: auto;
            }
          }
          .content_balloon_wrapper {
            border-radius: 10px 0px 10px 10px;
          }
          .content {
            background-color: #0e9cf6;
            .ogp {
              .ogp_info {
                border-left: 4px solid white;
                .ogp_title {
                  color: white;
                }
                .ogp_description {
                  color: white;
                }
              }
            }
          }
        }
      }
      &.isOwnersTalk {
        .header {
          .content {
            background-color: #119cf638;
          }
        }
      }
    }
  }

  /* likeアクション */
  .likeAnime {
    animation-name: likeInAnime;
    animation-duration: 0.25s;
  }
  @keyframes likeInAnime {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.5);
    }
    100% {
      transform: scale(1);
    }
  }
  #chakra1,
  #chakra2,
  #chakra3,
  #chakra4,
  #chakra5,
  #chakra6,
  #chakra7,
  #chakra8,
  #chakra9,
  #chakra10 {
    position: absolute;
    border-radius: 100;
    display: none;
    opacity: 0;
  }
  /* chakra1 */
  @keyframes chankraAnime1 {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.2;
    }
    100% {
      opacity: 0.1;
      transform: translate(0px, -10px) scale(1.6);
    }
  }
  #chakra1 {
    top: -5px;
    left: 10px;
    width: 3px;
    height: 3px;
    background: #cc8ef5;
    &.likeAnime {
      display: block;
      animation-name: chankraAnime1;
      animation-duration: 0.25s;
    }
  }
  /* chakra2 */
  @keyframes chankraAnime2 {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.2;
    }
    100% {
      opacity: 0.1;
      transform: translate(-10px, -10px) scale(1.4);
    }
  }
  #chakra2 {
    top: 1px;
    left: -6px;
    width: 4px;
    height: 4px;
    background: #cc8ef5;
    &.likeAnime {
      display: block;
      animation-name: chankraAnime2;
      animation-duration: 0.25s;
    }
  }
  /* chakra3 */
  @keyframes chankraAnime3 {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.2;
    }
    100% {
      opacity: 0.1;
      transform: translate(-10px, 10px) scale(2);
    }
  }
  #chakra3 {
    top: 20px;
    left: -8px;
    width: 3px;
    height: 3px;
    background: #91d2fa;
    &.likeAnime {
      display: block;
      animation-name: chankraAnime3;
      animation-duration: 0.25s;
    }
  }
  /* chakra4 */
  @keyframes chankraAnime4 {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.2;
    }
    100% {
      opacity: 0.1;
      transform: translate(-10px, 10px) scale(1.2);
    }
  }
  #chakra4 {
    bottom: 10px;
    left: 0px;
    width: 2px;
    height: 2px;
    background: #fff450;
    &.likeAnime {
      display: block;
      animation-name: chankraAnime4;
      animation-duration: 0.25s;
    }
  }
  /* chakra5 */
  @keyframes chankraAnime5 {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.2;
    }
    100% {
      opacity: 0.1;
      transform: translate(-10px, 10px) scale(1.2);
    }
  }
  #chakra5 {
    bottom: 0px;
    left: 0px;
    width: 3px;
    height: 3px;
    background: #cc8ef5;
    &.likeAnime {
      display: block;
      animation-name: chankraAnime5;
      animation-duration: 0.25s;
    }
  }
  /* chakra6 */
  @keyframes chankraAnime6 {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.2;
    }
    100% {
      opacity: 0.1;
      transform: translate(10px, -10px) scale(1.1);
    }
  }
  #chakra6 {
    top: -6px;
    right: 6px;
    width: 4px;
    height: 4px;
    background: #8ce8c3;
    &.likeAnime {
      display: block;
      animation-name: chankraAnime6;
      animation-duration: 0.25s;
    }
  }
  /* chakra7 */
  @keyframes chankraAnime7 {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.2;
    }
    100% {
      opacity: 0.1;
      transform: translate(10px, -10px) scale(2);
    }
  }
  #chakra7 {
    top: 4px;
    right: -4px;
    width: 3px;
    height: 3px;
    background: #fff450;
    &.likeAnime {
      display: block;
      animation-name: chankraAnime7;
      animation-duration: 0.25s;
    }
  }
  /* chakra8 */
  @keyframes chankraAnime8 {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.2;
    }
    100% {
      opacity: 0.1;
      transform: translate(10px, 0px) scale(1.5);
    }
  }
  #chakra8 {
    top: 14px;
    right: -10px;
    width: 3px;
    height: 3px;
    background: #91d2fa;
    &.likeAnime {
      display: block;
      animation-name: chankraAnime8;
      animation-duration: 0.25s;
    }
  }
  /* chakra9 */
  @keyframes chankraAnime9 {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.2;
    }
    100% {
      opacity: 0.1;
      transform: translate(10px, 10px) scale(2);
    }
  }
  #chakra9 {
    bottom: 8px;
    right: 0px;
    width: 2px;
    height: 2px;
    background: #91d2fa;
    &.likeAnime {
      display: block;
      animation-name: chankraAnime9;
      animation-duration: 0.25s;
    }
  }
  /* chakra10 */
  @keyframes chankraAnime10 {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.2;
    }
    100% {
      opacity: 0.1;
      transform: translate(10px, 10px) scale(2);
    }
  }
  #chakra10 {
    bottom: 1px;
    right: 8px;
    width: 2px;
    height: 2px;
    background: #fff450;
    &.likeAnime {
      display: block;
      animation-name: chankraAnime10;
      animation-duration: 0.25s;
    }
  }
  .show-in-room {
    text-align: left;
    color: rgb(var(--v-theme-monochrome-02)) !important;
    font-size: 12px;
  }
  .talk-found {
    animation-name: jiggle;
    animation-duration: 0.5s;
  }
  @keyframes jiggle {
    0% {
      transform: translate(1px, 1px) rotate(0deg);
    }
    10% {
      transform: translate(-1px, -2px) rotate(-1deg);
    }
    20% {
      transform: translate(-3px, 0px) rotate(1deg);
    }
    30% {
      transform: translate(3px, 2px) rotate(0deg);
    }
    40% {
      transform: translate(1px, -1px) rotate(1deg);
    }
    50% {
      transform: translate(-1px, 2px) rotate(-1deg);
    }
    60% {
      transform: translate(-3px, 1px) rotate(0deg);
    }
    70% {
      transform: translate(3px, 1px) rotate(-1deg);
    }
    80% {
      transform: translate(-1px, -1px) rotate(1deg);
    }
    90% {
      transform: translate(1px, 2px) rotate(0deg);
    }
    100% {
      transform: translate(1px, -2px) rotate(-1deg);
    }
  }
</style>
<style lang="scss">
  .isMe {
    .message_link {
      color: white;
    }
  }
  .search_word {
    background-color: rgb(var(--v-theme-main-04));
  }
  .update_mark {
    font-size: 12px;
    color: #dddddd;
  }
</style>
